import React, { useEffect, useState } from 'react';
import Dialog from '../Dialog';
import { Loader2, RefreshCcw } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';
interface SyncDialogNewProps {
  isOpen: boolean;
  onClose: () => void;
  handleSync: (status: string, progress: number, message: string) => void;
  getUsers?: (page?: number) => Promise<void>;
  isOnline: boolean;
  jobsiteId?: string;
}

const SyncDialogNew: React.FC<SyncDialogNewProps> = ({
  isOpen,
  onClose,
  handleSync,
  getUsers,
  isOnline,
  jobsiteId
}) => {
  const { isOnline: isOnlineFromHook } = useNetworkStatus();
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState('Preparing to sync...');
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncMessage, setSyncMessage] = useState('');
  const [syncDetail, setSyncDetail] = useState<{ jobSiteNames: string[], jobSiteName: string } | null>(null);
  
  // Reset values when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSyncStatus('');
      setSyncDetail(null);
      setSyncMessage('');
      setSyncProgress(0);
      setIsSyncing(false);
    }
  }, [isOpen]);

  const handleSyncClick = async () => {
    if (!isOnline) {
      toast.error('Cannot sync while offline. Please connect to the internet.');
      return;
    }

    if (isSyncing) {
      return; // Prevent multiple sync requests
    }

    setIsSyncing(true);
    // toast.info('Synchronizing data...');

    try {
      // Get the active service worker registration
      const registration = await navigator.serviceWorker.ready;
      
      if (!registration.active) {
        throw new Error('Service worker not active');
      }

      // Trigger the sync by sending a message to the service worker
      registration.active.postMessage({
        type: 'MANUAL_SYNC',
        timestamp: Date.now(),
        jobsiteId: jobsiteId
      });


      setTimeout(() => {
        setIsSyncing(false);
         if(syncStatus !== 'COMPLETED') {
          setSyncStatus('COMPLETED');
          setSyncDetail({ jobSiteNames: [], jobSiteName: '' });
          const defaultSyncMessage = 'Sync process completed successfully.';
          setSyncMessage(defaultSyncMessage);
          setSyncProgress(100);
          handleSync(syncStatus, syncProgress, defaultSyncMessage);
         } else {
          handleSync(syncStatus, syncProgress, '');
         }
      }, 10000);

    } catch (error) {
      console.error('Sync error:', error);
      toast.error(`Sync failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setIsSyncing(false);
    }
  };

  

  const handleClose = (status: string) => {
    setSyncStatus('');
    setSyncDetail(null);
    setSyncMessage('');
    setSyncProgress(0);
    onClose();
  }

  useEffect(() => {
    if ('serviceWorker' in navigator) {
        const channel = new MessageChannel();

        navigator.serviceWorker.ready.then((registration) => {
            // Send the port to the service worker
            registration.active?.postMessage(
                {
                    type: 'INIT_PORT',
                    // Optional: pass any initialization data 
                },
                [channel.port2]
            );

            // Set up message listener
            channel.port1.onmessage = async (event) => {
                // Handle messages from the service worker
                switch (event.data.type) {
                    case 'GET_OFFLINE_DOCS':
                        break;
                    case 'GET_ONLINE_DOCS':
                        break;
                    case 'SYNC_START':
                        console.log('Sync started: In Jobsite Management', event.data.message);
                        setSyncStatus('STARTED');
                        setSyncProgress(5);
                        setSyncMessage(event.data.message);
                        break;
                    case 'SYNC_PROGRESS':
                        setSyncStatus('INPROGRESS');
                        setSyncProgress(50);
                        setSyncMessage(event.data.message);
                        break;
                    case 'SYNC_SUCCESS':
                        setSyncStatus('SUCCESS');
                        setSyncProgress(100);
                        setSyncMessage(event.data.message);
                        break;
                    case 'SYNC_ERROR':
                        setSyncStatus('ERROR');
                        setSyncDetail(event.data);
                        setSyncMessage(event.data.message);
                        setSyncProgress(100);
                        setIsSyncing(false);
                        handleSync(syncStatus, syncProgress, '');
                        break;
                    case 'SYNC_COMPLETE':
                        setSyncStatus('COMPLETED');
                        setSyncDetail(event.data);
                        setSyncMessage(event.data.message);
                        setSyncProgress(100);
                        setIsSyncing(false);
                        handleSync(syncStatus, syncProgress, '');
                        break;
                }
            };
        });

        return () => {
            // Cleanup if needed
            channel.port1.close();
        };
    }
}, [handleSync, syncProgress, syncStatus]);


  return (
    <Dialog
      isOpen={isOpen}
      onClose={() => {
        // Only allow closing if there was an error or sync is complete
        if (syncProgress === 100 || syncStatus.includes('error')) {
          onClose();
        }
      }}
      width="w-[400px]"
      padding="p-6"
      disableBackdropClick={syncProgress !== 100 && !syncStatus.includes('error')}
      hideCloseButton={true}
    >
      <div className="text-center">
        <h3 className="text-xl font-medium mb-4">Offline Data Sync</h3>
        {(syncStatus.includes('COMPLETED') || syncStatus.includes('ERROR') || 
        syncStatus.includes('STARTED') || syncStatus.includes('INPROGRESS') || 
        syncStatus.includes('SUCCESS') ||
        syncStatus !== ''
      ) ? <div></div> : 
        <div>
          <div className="flex justify-center items-center mb-5 mt-5">
            <h2 className="text-sm font-medium flex items-center justify-center">
              Please sync your data to continue 
            </h2>

          </div>
          <div className="flex justify-center items-center mb-5 mt-5">
            <button 
              onClick={handleSyncClick}
              disabled={!isOnlineFromHook || isSyncing}
              className={`flex items-center justify-center px-4 py-2 rounded-md bg-[#A42823] text-white transition-colors ${
                !isOnlineFromHook ? 'cursor-not-allowed opacity-50' : 'hover:bg-[#8a2320]'
              }`}
              title={isOnlineFromHook ? 'Sync data' : 'Cannot sync while offline'}
              aria-label="Sync data"
              id="sync-dialog-sync-button">
                  {
                    isSyncing ? (
                      <Loader2 size={18} className="animate-spin mr-2" />
                    ) : (
                      <RefreshCcw size={18} className="mr-2" />
                    )
                  }
                  Sync
            </button> 
          </div>
        </div>
        }
        <div className="flex justify-center mb-5">
          {syncStatus.includes('ERROR') && (
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center text-red-500">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
          ) }
          { syncStatus.includes('COMPLETED') && (
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-green-500">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
        </div>
        
        {syncStatus.includes('STARTED') && <div className="text-blue-700 mb-3">{syncStatus}</div> }
        {syncStatus.includes('INPROGRESS') && <div className="text-blue-700 mb-3">{syncStatus}</div> }
        {syncStatus.includes('SUCCESS') && <div className="text-green-700 mb-3">{syncStatus}</div> }
        {syncStatus.includes('ERROR') && <div className="text-red-700 mb-3">{syncStatus}</div> }
        {syncStatus.includes('COMPLETED') && <div className="text-green-700 mb-3">{syncStatus}</div> }

        {syncStatus.includes('ERROR') && (
          <>
            { syncDetail?.jobSiteName && <div className="text-red-900 mb-3">Jobsite Name: {syncDetail?.jobSiteName} </div> }
            <div className="text-red-700 mb-3">{syncMessage}</div>
          </>
        )}
        {syncStatus.includes('COMPLETED') && (
          <>
            <div className="text-gray-700 mb-3">{syncMessage}</div>
            <div className="text-gray-700 mb-3">{syncDetail?.jobSiteName}</div>
          </>
        )}

 
        
        <p className="text-gray-500 text-sm mb-6">
          {syncProgress < 100 && !syncStatus.includes('error') 
            ? "Please wait while we synchronize your offline data with the server. This process may take a moment."
            : syncStatus.includes('error')
              ? "There was an error synchronizing your data. You can try again later."
              : ""}
        </p>
        
        {(syncStatus.includes('ERROR') || syncStatus.includes('COMPLETED')) && (
          <button
            onClick={() => handleClose(syncStatus)}
            className="btn-primary px-8"
            id="sync-dialog-close-button"
          >
            {syncStatus.includes('ERROR') ? "Close" : "Done"}
          </button>
        )}
      </div>
    </Dialog>
  );
};

export default SyncDialogNew;